import React, { useCallback, useReducer } from 'react';
import { createRestaurantReducer } from './createRestaurantReducer';
import {
	CreateRestaurantContext,
	createRestaurantInitialState,
	TCreateRestaurantSelectedClientType,
	TCreateRestaurantSelectedItem,
	TCreateRestaurantSelectedPOSType,
	TTypeOfCreateRestaurantModalWindow,
} from './createRestaurantContext';
import { CreateRestaurantActionTypes } from './createRestaurantActions';
import { ICurrencies, IItemForGetShortInfo, ICreateRestaurant } from 'types';

const CreateRestaurantState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createRestaurantReducer, createRestaurantInitialState);

	const setFetchedCountries = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_FETCHED_COUNTRIES,
			payload: {
				fetchedCountries: value,
			},
		});
	}, []);

	const setIDCountry = useCallback((value: TCreateRestaurantSelectedItem) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_ID_COUNTRY,
			payload: {
				IDSelectedCountry: value,
			},
		});
	}, []);

	const setFetchedCities = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_FETCHED_CITIES,
			payload: {
				fetchedCities: value,
			},
		});
	}, []);

	const setIDCity = useCallback((value: TCreateRestaurantSelectedItem) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_ID_CITY,
			payload: {
				IDSelectedCity: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback(
		(value: TCreateRestaurantSelectedItem) => {
			dispatch({
				type: CreateRestaurantActionTypes.SET_ID_CUSTOMER,
				payload: {
					IDSelectedCustomer: value,
					isUsedCustomerPriceType: value ? state.isUsedCustomerPriceType : false,
				},
			});
		},
		[state.isUsedCustomerPriceType],
	);

	const setClientType = useCallback((value: TCreateRestaurantSelectedClientType) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_CLIENT_TYPE,
			payload: {
				clientType: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedCurrencies = useCallback((value: ICurrencies[]) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_FETCHED_CURRENCIES,
			payload: {
				fetchedCurrencies: value,
			},
		});
	}, []);

	const setCurrencySymbol = useCallback((value: TCreateRestaurantSelectedItem) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_CURRENCY_SYMBOL,
			payload: {
				selectedCurrencySymbol: value,
			},
		});
	}, []);

	const changeAddress = useCallback((value: string) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_ADDRESS,
			payload: {
				address: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changeCode = useCallback((value: string) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_CODE,
			payload: {
				code: value,
			},
		});
	}, []);

	const changePriceType = useCallback((value: TCreateRestaurantSelectedItem) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_PRICE_TYPE,
			payload: {
				priceType: value,
			},
		});
	}, []);

	const toggleIsUsedCustomerPriceType = useCallback(() => {
		dispatch({
			type: CreateRestaurantActionTypes.TOGGLE_IS_USED_CUSTOMER_PRICE_TYPE,
			payload: {
				isUsedCustomerPriceType: !state.isUsedCustomerPriceType,
				priceType: state.isUsedCustomerPriceType ? state.priceType : null,
			},
		});
	}, [state.isUsedCustomerPriceType, state.priceType]);

	const setIDPosSystem = useCallback((value: TCreateRestaurantSelectedPOSType) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_ID_POS_SYSTEM,
			payload: {
				IDPosSystem: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				fetchedLanguages: value,
			},
		});
	}, []);

	const setIDDefaultLanguage = useCallback((value: TCreateRestaurantSelectedItem) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_ID_DEFAULT_LANGUAGE,
			payload: {
				IDSelectedDefaultLanguage: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: ICreateRestaurant | null) => {
		dispatch({
			type: CreateRestaurantActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateRestaurantModalWindow) => {
		dispatch({
			type: CreateRestaurantActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);
	const resetFields = useCallback(() => dispatch({ type: CreateRestaurantActionTypes.RESET_FIELDS }), []);

	return (
		<CreateRestaurantContext.Provider
			value={{
				setFetchedCountries,
				setIDCountry,
				setFetchedCities,
				setIDCity,
				setFetchedCustomers,
				setIDCustomer,
				setClientType,
				changeName,
				setFetchedCurrencies,
				setCurrencySymbol,
				changeAddress,
				changeEmail,
				changeCode,
				changePriceType,
				toggleIsUsedCustomerPriceType,
				setIDPosSystem,
				setFetchedLanguages,
				setIDDefaultLanguage,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fetchedCountries: state.fetchedCountries,
				IDSelectedCountry: state.IDSelectedCountry,
				fetchedCities: state.fetchedCities,
				IDSelectedCity: state.IDSelectedCity,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				clientType: state.clientType,
				name: state.name,
				fetchedCurrencies: state.fetchedCurrencies,
				selectedCurrencySymbol: state.selectedCurrencySymbol,
				address: state.address,
				email: state.email,
				code: state.code,
				priceType: state.priceType,
				isUsedCustomerPriceType: state.isUsedCustomerPriceType,
				IDPosSystem: state.IDPosSystem,
				fetchedLanguages: state.fetchedLanguages,
				IDSelectedDefaultLanguage: state.IDSelectedDefaultLanguage,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateRestaurantContext.Provider>
	);
};

export default CreateRestaurantState;
